import { createSlice, type PayloadAction, createAsyncThunk } from '@reduxjs/toolkit'
import { GlobalClientAuthService } from '@sevenrooms/api'
import type { CountryCode } from '@sevenrooms/domain'
import type { UserPreference, UserSignificantDate } from '@sevenrooms/ui-kit/core'

export type ClientLoginProvider = 'google' | 'facebook' | 'sevenrooms'

export interface ClientLoginState {
  isGettingClaims: boolean
  isGettingUser: boolean
  isLoggingOut: boolean
  provider?: ClientLoginProvider
  clientId?: string
  firstName?: string
  lastName?: string
  emailAddress?: string
  profilePictureUrl?: string
  phoneNumber?: string
  phoneCountryCode?: CountryCode
  phoneDialCode?: string
  significantDates?: UserSignificantDate[]
  preferences?: UserPreference[]
}

const sevenRoomsLogoutAction = createAsyncThunk('clientLogin/sevenrooms/logout', async (_, { dispatch }) => {
  await GlobalClientAuthService.logout()
  dispatch(clientLoginActions.logout())
})

const getSevenRoomsClientClaimsAction = createAsyncThunk('clientLogin/sevenrooms/getClaims', async () =>
  GlobalClientAuthService.getExistingLoginCredentials()
)

const getSevenRoomsUserAction = createAsyncThunk('clientLogin/sevenrooms/getUser', async () => GlobalClientAuthService.getUser())

const initialState: ClientLoginState = {
  isGettingClaims: false,
  isGettingUser: false,
  isLoggingOut: false,
}

const clientLoginSlice = createSlice({
  name: 'clientLogin',
  initialState,
  reducers: {
    login: (state, action: PayloadAction<Omit<ClientLoginState, 'isGettingClaims' | 'isGettingUser' | 'isLoggingOut'>>) => {
      const { payload: newState } = action
      return { ...newState, isGettingClaims: state.isGettingClaims, isLoggingOut: state.isLoggingOut, isGettingUser: state.isGettingUser }
    },
    logout: _state => initialState,
  },
  extraReducers: builder => {
    builder.addCase(sevenRoomsLogoutAction.pending, state => {
      state.isLoggingOut = true
    })
    builder.addCase(sevenRoomsLogoutAction.rejected, state => {
      state.isLoggingOut = false
    })
    builder.addCase(sevenRoomsLogoutAction.fulfilled, state => {
      state.isLoggingOut = false
    })
    builder.addCase(getSevenRoomsClientClaimsAction.pending, state => {
      state.isGettingClaims = true
    })
    builder.addCase(getSevenRoomsClientClaimsAction.rejected, state => {
      state.isGettingClaims = false
    })
    builder.addCase(getSevenRoomsClientClaimsAction.fulfilled, (state, action) => {
      const { payload: clientClaims } = action
      if (!clientClaims) {
        return { ...state, isGettingClaims: false }
      }
      return { provider: 'sevenrooms', ...clientClaims, isGettingClaims: false, isGettingUser: false, isLoggingOut: state.isLoggingOut }
    })
    builder.addCase(getSevenRoomsUserAction.pending, state => {
      state.isGettingUser = true
    })
    builder.addCase(getSevenRoomsUserAction.rejected, state => {
      state.isGettingUser = false
    })
    builder.addCase(getSevenRoomsUserAction.fulfilled, (state, action) => {
      const { preferences, profilePictureUrl, significantDates } = action.payload
      return {
        ...state,
        profilePictureUrl,
        preferences,
        significantDates,
        isGettingUser: false,
      }
    })
  },
})

export const clientLoginActions = {
  ...clientLoginSlice.actions,
  getSevenRoomsClientClaimsAction,
  sevenRoomsLogoutAction,
  getSevenRoomsUserAction,
}
export const clientLoginReducer = clientLoginSlice.reducer
export const clientLoginName = clientLoginSlice.name
